import React, { useContext } from 'react'
import { array, object, shape, string, bool } from 'prop-types'
import styled from '@emotion/styled'
import { useInView } from 'react-intersection-observer'
import { isBrowser, useArticle, useToc } from '@jelly/utils'

// Import Components
import { ContentBody } from './ContentBody'
import {
	AuthorCard,
	BackToTopSticky as BackToTop,
	ContentHeader,
	EmailSubscribeContext,
	RelatedRail,
	RelatedEndTiles,
	SiteConfigContext,
	TableOfContents,
} from '@jelly/components'

// Styled Components via Emotion
const BodyContainer = styled.div`
	display: block;
	height: 100%;
	margin: 2.25rem auto;
	width: 90%;

	${({ theme }) => theme.MQ.M} {
		display: flex;
		flex-direction: row;
	}

	${({ theme }) => theme.MQ.XL} {
		${({ theme }) => theme.maxWidth.L};
		padding: 0 1rem;
		width: 100%;
	}
`
const ContentWrapper = styled.div`
	width: 100%;
`
const LeftRailComponents = styled.div`
	display: none;
	margin-right: 25px;
	position: relative;
	width: 20%;
	min-width: 200px;

	${({ theme }) => theme.MQ.L} {
		display: inline-block;
	}
`
const TocContainer = styled.div`
	position: absolute;
	height: 100%;
`
const MainContent = styled.div`
	display: block;
	font-weight: 300;
	height: 100%;
	margin: 0 auto;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		display: flex;
		flex-direction: column;
		width: 100%;
	}
`
const MainBody = styled.div`
	display: flex;
	flex-direction: column;

	${({ theme }) => theme.MQ.M} {
		flex-direction: row;
	}
`
const ImageCreditWrapper = styled.div`
	padding: 1rem 0.25rem;
	width: 100%;
`
const ImageCredit = styled.div`
	font-size: 9px;
	text-align: right;

	${({ theme }) => theme.MQ.M} {
		font-size: 12px;
	}
`
const RightRailComponents = styled.aside`
	display: block;
	margin-left: auto;
	margin-top: 2rem;
	min-width: 100%;
	position: relative;
	right: 0;

	${({ theme }) => theme.MQ.M} {
		display: inline-block;
		margin-left: 25px;
		margin-top: 0;
		min-width: ${({ hasRightRailContent }) =>
			hasRightRailContent ? '300px' : '110px'}; // min-width required for long form variation
	}
`
const BackToTopContainer = styled.div`
	display: none;
	${({ theme }) => theme.MQ.M} {
		display: block;
	}
`

function useEmailSignupDisplayProps({ isSingleSponsorOrAdvertorial = false }) {
	const {
		siteConstants: {
			EMAIL_CAPTURE_SOURCES: { article },
		},
	} = useContext(SiteConfigContext)
	const { emailSubscribeFormSubmitted, setEmailSubscribeSource, setIsScrollListenerEnabled } = useContext(
		EmailSubscribeContext
	)

	setEmailSubscribeSource(article)

	const [emailCaptureRef, emailCaptureInView] = useInView({
		triggerOnce: true,
	})
	const hasRfqWidget =
		isBrowser() &&
		(document.getElementsByClassName('bz-form-widget').length > 0 ||
			document.getElementsByClassName('widget-post-init').length > 0)
	const hasQtWidget = isBrowser() && [].slice.call(document.querySelectorAll('[data-qt-pub-id]')).length > 0 // this is the es5 compatible equivelent (https://davidwalsh.name/nodelist-array)
	const hasBestPickRibbon = isBrowser() && document.getElementById('best-pick-ribbon')
	const hasWidget = hasRfqWidget || hasQtWidget || hasBestPickRibbon
	const shouldShowEmailCapture = !isSingleSponsorOrAdvertorial

	if (!hasWidget && !emailSubscribeFormSubmitted) {
		setIsScrollListenerEnabled(true)
	} else {
		setIsScrollListenerEnabled(false)
	}

	return {
		emailCaptureRef,
		shouldShowEmailCapture,
		shouldShowEmailCaptureBox: shouldShowEmailCapture && hasWidget && emailCaptureInView,
	}
}

const propTypes = {
	author: object.isRequired,
	bestPicks: array,
	byLine: string.isRequired,
	channels: shape({
		primary: object.isRequired,
		sub: object,
	}).isRequired,
	isSingleSponsorOrAdvertorial: bool,
	mainContent: string.isRequired,
	lastModified: string.isRequired,
	parsedContent: object.isRequired,
	published: string.isRequired,
	relatedArticles: array,
	relatedExperts: array,
	relatedReviews: array,
	relatedSponsoredArticles: array,
	resource: object,
	sponsorship: object,
	subtitle: string,
	thumbnail: object,
	type: string,
}

/**
 * This is the Body of the Article template
 * Contains a sticky Table of Contents, context, related right rail
 * and sticky back to top button
 *
 * Methods at the bottom to support some sticky features (some are just for IE11)
 */
export function ArticleBody({
	author,
	bestPicks,
	byLine,
	channels,
	docType,
	lastModified,
	parsedContent,
	published,
	relatedArticles,
	relatedExperts,
	relatedReviews,
	relatedEndArticles,
	relatedSponsoredArticles,
	resource,
	sponsorship,
	subtitle,
	thumbnail,
	title,
	tocElements,
	template,
	type,
}) {
	const {
		siteConstants: { ADVERTORIAL_SINGLE_SPONSOR, LONG_FORM },
		siteComponents: { EmailSignUpMessagingBox },
	} = useContext(SiteConfigContext)
	const isSingleSponsorOrAdvertorial = sponsorship && ADVERTORIAL_SINGLE_SPONSOR.includes(sponsorship.sponsorType)

	const {
		articleState: { didBackToTopHitFooter, shouldShowBackToTop },
		hasRightRailContent,
	} = useArticle([relatedArticles, relatedExperts, relatedReviews])

	const {
		tocState: { activeTocElement, isTocSticky, isFooterVisible, shouldShowToc, topOffset },
	} = useToc(parsedContent.toc)

	const { emailCaptureRef, shouldShowEmailCaptureBox } = useEmailSignupDisplayProps({
		isSingleSponsorOrAdvertorial,
	})

	const { caption } = thumbnail

	return (
		<>
			<BodyContainer id="top-of-toc">
				{!!parsedContent.toc && shouldShowToc && (
					<LeftRailComponents>
						<TocContainer>
							<TableOfContents
								activeTocElement={activeTocElement}
								docType={docType}
								isFooterVisible={isFooterVisible}
								isSticky={isTocSticky}
								tocElements={parsedContent.toc}
								topOffset={topOffset}
							/>
						</TocContainer>
					</LeftRailComponents>
				)}
				<MainContent>
					<MainBody isTocPresent={shouldShowToc}>
						<ContentWrapper>
							<ContentHeader
								author={author}
								byLine={byLine}
								lastModified={lastModified}
								published={published}
								sponsorship={sponsorship}
								title={title}
							/>
							<ContentBody
								bestPicks={bestPicks}
								json={parsedContent.json}
								resource={resource}
								subtitle={subtitle}
								template={template}
								type={type}
							/>
							{caption && (
								<ImageCreditWrapper>
									<ImageCredit>Image Credit: {caption}</ImageCredit>
								</ImageCreditWrapper>
							)}
							<div ref={emailCaptureRef}>{shouldShowEmailCaptureBox && <EmailSignUpMessagingBox />}</div>
							{!isSingleSponsorOrAdvertorial && !byLine && <AuthorCard author={author} />}
						</ContentWrapper>
						{template !== LONG_FORM && (
							<RightRailComponents hasRightRailContent={hasRightRailContent}>
								<RelatedRail
									category={channels}
									hasRightRailContent={hasRightRailContent} // will have to refactor later on when we want to use a dfp flag
									relatedArticles={relatedArticles}
									relatedExperts={relatedExperts}
									relatedReviews={relatedReviews}
									relatedSponsoredArticles={relatedSponsoredArticles}
									sponsorship={sponsorship}
									isSingleSponsorOrAdvertorial={isSingleSponsorOrAdvertorial}
								/>
								{shouldShowBackToTop && (
									<BackToTopContainer>
										<BackToTop hitFooter={didBackToTopHitFooter} />
									</BackToTopContainer>
								)}
							</RightRailComponents>
						)}
					</MainBody>

					{relatedEndArticles && !!relatedEndArticles.length && !isSingleSponsorOrAdvertorial && (
						<RelatedEndTiles listOfContent={relatedEndArticles} channel={channels?.primary} />
					)}
				</MainContent>
			</BodyContainer>
		</>
	)
}

ArticleBody.propTypes = propTypes
